import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { type ApiError, api, qs, unwrap } from '../api/client';
import { useAccounts, useContactsIndex } from '../api/hooks';
import type {
  CommentedMedia,
  Conversation,
  ConversationMessage,
  InboxComment,
  Items,
  Page,
  RepliedComment,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  DataTable,
  Empty,
  ErrorAlert,
  fmtDate,
  PageHeader,
  relTime,
  useToast,
} from '../components/ui';

type InboxTab = 'comments' | 'dms' | 'reviews';
const isTab = (v: string | null): v is InboxTab =>
  v === 'comments' || v === 'dms' || v === 'reviews';

export function InboxPage() {
  const [params] = useSearchParams();
  const wanted = params.get('tab');
  const [tab, setTab] = useState<InboxTab>(isTab(wanted) ? wanted : 'comments');
  return (
    <>
      <PageHeader
        title="Conversations"
        subtitle="Comments, direct messages and replies across your connected accounts"
      />
      <div className="tabs">
        {(
          [
            ['comments', 'Comments'],
            ['dms', 'Direct messages'],
            ['reviews', 'Reviews & replies'],
          ] as const
        ).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={tab === k ? 'active' : ''}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      {tab === 'comments' ? <CommentsTab /> : null}
      {tab === 'dms' ? <DmsTab /> : null}
      {tab === 'reviews' ? <ReviewsTab /> : null}
    </>
  );
}

function CommentsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const accounts = useAccounts();
  const [filter, setFilter] = useState({
    status: 'all',
    accountId: '',
    q: '',
    mediaExternalId: '',
  });
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const posts = useQuery({
    queryKey: ['inbox-posts', filter.accountId],
    queryFn: () =>
      unwrap(
        api.get<Items<CommentedMedia>>(`/portal/inbox/posts${qs({ accountId: filter.accountId })}`),
      ),
  });
  const [text, setText] = useState('');
  const q = useQuery({
    queryKey: ['inbox-comments', filter],
    queryFn: () => unwrap(api.get<Page<InboxComment>>(`/portal/inbox/comments${qs(filter)}`)),
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['inbox-comments'] });
    await qc.invalidateQueries({ queryKey: ['inbox-posts'] });
  };
  const reply = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/portal/inbox/comments/${id}/reply`, { text })),
    onSuccess: async () => {
      await refresh();
      setReplyFor(null);
      setText('');
      toast('Reply queued');
    },
  });
  const read = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/portal/inbox/comments/${id}/read`)),
    onSuccess: refresh,
  });
  const like = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/portal/inbox/comments/${id}/like`)),
    onSuccess: () => toast('Liked'),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const sync = useMutation({
    mutationFn: async () => {
      for (const a of accounts.data?.items ?? [])
        await unwrap(api.post(`/portal/social-accounts/${a.id}/sync`));
    },
    onSuccess: () => toast('Accounts refreshed'),
  });
  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <select
          className="input"
          style={{ maxWidth: 180 }}
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="open">Open (needs reply)</option>
        </select>
        <select
          className="input"
          style={{ maxWidth: 220 }}
          value={filter.accountId}
          onChange={(e) => setFilter({ ...filter, accountId: e.target.value })}
        >
          <option value="">All accounts</option>
          {(accounts.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              @{a.username}
            </option>
          ))}
        </select>
        <input
          className="input"
          style={{ maxWidth: 260 }}
          placeholder="Search text or username"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
        />
        <button
          type="button"
          className="btn"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
        >
          Refresh
        </button>
      </div>
      <ErrorAlert error={q.error} />
      <ErrorAlert error={reply.error as ApiError | null} />
      <div className="chat">
        <div className="chat-list" aria-label="Posts with comments">
          <button
            type="button"
            className={`chat-item ${filter.mediaExternalId === '' ? 'active' : ''}`}
            onClick={() => setFilter({ ...filter, mediaExternalId: '' })}
          >
            <strong>All posts</strong>
          </button>
          {(posts.data?.items ?? []).map((p) => (
            <button
              type="button"
              key={`${p.accountId}:${p.mediaExternalId}`}
              className={`chat-item ${filter.mediaExternalId === p.mediaExternalId ? 'active' : ''}`}
              onClick={() => setFilter({ ...filter, mediaExternalId: p.mediaExternalId })}
            >
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{p.caption ? p.caption.slice(0, 40) : p.mediaExternalId}</strong>
                {p.unread > 0 ? <span className="badge badge-danger">{p.unread}</span> : null}
              </div>
              <div className="small muted">
                {p.comments} comment{p.comments === 1 ? '' : 's'} · {relTime(p.lastCommentAt)}
              </div>
            </button>
          ))}
          {posts.data?.items.length === 0 ? <Empty>No comments yet.</Empty> : null}
        </div>
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Commenter</th>
                <th>Comment</th>
                <th>Media</th>
                <th>When</th>
                <th>State</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.items ?? []).map((c) => (
                <tr key={c.id} style={{ fontWeight: c.readAt ? 400 : 600 }}>
                  <td>@{c.audienceUsername ?? c.audienceUserId}</td>
                  <td>
                    {c.text}
                    {c.text ? (
                      <TranslateButton url={`/portal/inbox/comments/${c.id}/translate`} />
                    ) : null}
                    {replyFor === c.id ? (
                      <div className="row" style={{ marginTop: 6 }}>
                        <input
                          className="input"
                          autoFocus
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          placeholder="Public reply…"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={reply.isPending}
                          onClick={() => reply.mutate(c.id)}
                        >
                          Send
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setReplyFor(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                  </td>
                  <td className="mono">{c.mediaExternalId ?? '—'}</td>
                  <td>{relTime(c.occurredAt)}</td>
                  <td>
                    {c.replied ? <Badge value="replied" /> : <Badge value="open" />}{' '}
                    {c.readAt ? null : <Badge value="unread" />}
                  </td>
                  <td className="actions">
                    {can('conversation:reply') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setReplyFor(c.id)}
                      >
                        Reply
                      </button>
                    ) : null}{' '}
                    {can('conversation:reply') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => like.mutate(c.id)}
                      >
                        Like
                      </button>
                    ) : null}{' '}
                    {c.readAt ? null : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => read.mutate(c.id)}
                      >
                        Mark read
                      </button>
                    )}{' '}
                    {c.permalink ? (
                      <a className="btn btn-sm" href={c.permalink} target="_blank" rel="noreferrer">
                        View on Instagram
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
              {q.data?.items.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <Empty>No comments match these filters.</Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
      </div>
    </>
  );
}

/** INB-04: fetches a translation on demand and shows it under the original. */
function TranslateButton({ url }: { url: string }) {
  const m = useMutation({ mutationFn: () => unwrap(api.post<{ text: string }>(url)) });
  if (m.data) return <div className="small muted">Translation: {m.data.text}</div>;
  return (
    <div className="small">
      <button
        type="button"
        className="btn btn-link"
        disabled={m.isPending}
        onClick={() => m.mutate()}
      >
        View translation
      </button>
      {m.error ? <span className="muted"> {m.error.message}</span> : null}
    </div>
  );
}

/** INB-05: every comment the workspace answered, with the reply. UI-57: filtered by account. */
function ReviewsTab() {
  const accounts = useAccounts();
  const [accountId, setAccountId] = useState('');
  const q = useQuery({
    queryKey: ['inbox-replies', accountId],
    queryFn: () =>
      unwrap(
        api.get<Page<RepliedComment>>(
          `/portal/inbox/replies${qs({ accountId: accountId || undefined })}`,
        ),
      ),
  });
  return (
    <DataTable>
      <div className="row" style={{ marginBottom: 8 }}>
        <select
          className="input"
          style={{ maxWidth: 220 }}
          aria-label="Replies account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">All accounts</option>
          {(accounts.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              @{a.username}
            </option>
          ))}
        </select>
      </div>
      <ErrorAlert error={q.error} />
      <table className="table">
        <thead>
          <tr>
            <th>Commenter</th>
            <th>Comment</th>
            <th>Our reply</th>
            <th>By</th>
            <th>Replied</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).map((r) => (
            <tr key={r.replyId}>
              <td>@{r.comment.audienceUsername ?? r.comment.audienceUserId}</td>
              <td>{r.comment.text}</td>
              <td>{r.replyText}</td>
              <td>
                <Badge value={r.origin} />
              </td>
              <td>{r.repliedAt ? relTime(r.repliedAt) : 'queued'}</td>
            </tr>
          ))}
          {q.data?.items.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <Empty>No replies sent yet. Automations and manual replies appear here.</Empty>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </DataTable>
  );
}

function DmsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('');
  // UI-57: the account and unread filters the API already accepts
  const [accountId, setAccountId] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const accounts = useAccounts();
  const [selected, setSelected] = useState<string | null>(params.get('conversation'));
  const [text, setText] = useState('');
  const contacts = useContactsIndex();
  const labelFor = (c: Conversation): string => {
    const u = contacts.data?.items.find((x) => x.id === c.contactId)?.username;
    return u ? `@${u}` : `User ${c.audienceUserId}`;
  };
  const list = useQuery({
    queryKey: ['conversations', status, accountId, unreadOnly],
    queryFn: () =>
      unwrap(
        api.get<Page<Conversation>>(
          `/portal/conversations${qs({
            status: status || undefined,
            accountId: accountId || undefined,
            unread: unreadOnly ? 'true' : undefined,
          })}`,
        ),
      ),
    refetchInterval: 15_000,
  });
  const thread = useQuery({
    queryKey: ['conversation', selected],
    queryFn: () =>
      unwrap(
        api.get<{ conversation: Conversation; messages: ConversationMessage[] }>(
          `/portal/conversations/${selected}`,
        ),
      ),
    enabled: selected !== null,
    refetchInterval: 10_000,
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['conversations'] }),
      qc.invalidateQueries({ queryKey: ['conversation', selected] }),
    ]);
  const send = useMutation({
    mutationFn: () => unwrap(api.post(`/portal/conversations/${selected}/messages`, { text })),
    onSuccess: async () => {
      setText('');
      await refresh();
    },
  });
  const useAction = (verb: string, label: string) =>
    useMutation({
      mutationFn: () => unwrap(api.post(`/portal/conversations/${selected}/${verb}`)),
      onSuccess: async () => {
        await refresh();
        toast(label);
      },
      onError: (e: ApiError) => toast(e.message, 'error'),
    });
  const takeover = useAction('takeover', 'You took over this conversation');
  const close = useAction('close', 'Conversation closed');
  const read = useAction('read', 'Marked read');
  const conv = thread.data?.conversation;
  return (
    <div className="chat">
      <div>
        <select
          className="input"
          style={{ marginBottom: 8 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="handoff">Human takeover</option>
          <option value="closed">Closed</option>
        </select>
        <select
          className="input"
          style={{ marginBottom: 8 }}
          aria-label="DM account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">All accounts</option>
          {(accounts.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              @{a.username}
            </option>
          ))}
        </select>
        <label className="row small" style={{ alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          Unread only
        </label>
        <div className="chat-list">
          {/* UI-46: a failed list is said, not shown as empty */}
          <ErrorAlert error={list.error} />
          {(list.data?.items ?? []).map((c) => (
            <button
              type="button"
              key={c.id}
              className={selected === c.id ? 'active' : ''}
              onClick={() => {
                setSelected(c.id);
                if (c.unreadCount > 0) setTimeout(() => read.mutate(), 0);
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{labelFor(c)}</strong>
                {c.unreadCount > 0 ? (
                  <span className="badge badge-danger">{c.unreadCount}</span>
                ) : null}
              </div>
              <div className="small muted">
                <Badge value={c.status} /> · {relTime(c.lastMessageAt)}
              </div>
            </button>
          ))}
          {list.data?.items.length === 0 ? <Empty>No conversations yet.</Empty> : null}
        </div>
      </div>
      <div className="chat-thread">
        {conv ? (
          <>
            <div
              className="row"
              style={{
                padding: 12,
                borderBottom: '1px solid var(--border)',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <strong>{labelFor(conv)}</strong> <Badge value={conv.status} />
              </div>
              <div className="actions">
                {can('conversation:reply') && conv.status !== 'handoff' ? (
                  <button type="button" className="btn btn-sm" onClick={() => takeover.mutate()}>
                    Human takeover
                  </button>
                ) : null}
                {conv.status !== 'closed' ? (
                  <button type="button" className="btn btn-sm" onClick={() => close.mutate()}>
                    Close
                  </button>
                ) : null}
              </div>
            </div>
            <div className="messages">
              {(thread.data?.messages ?? []).map((m) => (
                <div key={m.id} className={`bubble ${m.direction}`}>
                  <div>{m.text}</div>
                  {m.direction === 'in' && m.text && conv ? (
                    <TranslateButton
                      url={`/portal/conversations/${conv.id}/messages/${m.id}/translate`}
                    />
                  ) : null}
                  <div className="meta">
                    {fmtDate(m.createdAt)}
                    {m.direction === 'out' && m.readAt ? ' · read' : ''}
                  </div>
                </div>
              ))}
            </div>
            <ErrorAlert error={send.error as ApiError | null} />
            {can('conversation:reply') ? (
              <form
                className="composer"
                onSubmit={(e) => {
                  e.preventDefault();
                  send.mutate();
                }}
              >
                <input
                  className="input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a reply…"
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={send.isPending || !text.trim()}
                >
                  Send
                </button>
              </form>
            ) : null}
          </>
        ) : selected && thread.isError ? (
          // UI-46 (revised): a thread that is gone says so; any other failure shows the error
          (thread.error as { status?: number } | null)?.status === 404 ? (
            <Empty>This conversation is no longer available.</Empty>
          ) : (
            <ErrorAlert error={thread.error} />
          )
        ) : (
          <Empty>Select a conversation</Empty>
        )}
      </div>
    </div>
  );
}
